import { type ScheduleItem, occupiesDay as sharedOccupiesDay } from '@apm/contracts';

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` → mốc UTC. Không đi qua timezone của trình duyệt. */
export function parseIso(date: string): number {
  return Date.parse(`${date}T00:00:00Z`);
}

export function toIso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Thứ Hai của tuần chứa `date`.
 *
 * Tuần bắt đầu từ thứ Hai theo thói quen Việt Nam, không phải Chủ nhật kiểu Mỹ.
 */
export function startOfWeek(date: string): string {
  const ms = parseIso(date);
  const dayOfWeek = new Date(ms).getUTCDay(); // 0 = CN
  const offset = (dayOfWeek + 6) % 7;
  return toIso(ms - offset * DAY_MS);
}

export function addDays(date: string, days: number): string {
  return toIso(parseIso(date) + days * DAY_MS);
}

/** Danh sách ngày liên tiếp `[from, to]`, bao gồm cả hai đầu. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let ms = parseIso(from); ms <= parseIso(to); ms += DAY_MS) out.push(toIso(ms));
  return out;
}

/**
 * Một mục có "chiếm" ngày này không.
 *
 * Task có cả hai mốc thì chiếm trọn khoảng giữa — không chỉ hai đầu mút. Đây là
 * điểm khiến calendar khác một danh sách sắp theo hạn: một việc kéo ba tuần
 * phải hiện ở cả ba tuần.
 *
 * Luật nằm ở `@apm/contracts` (APM-MYT-b): lịch đội ở API **đếm** theo đúng hàm
 * ấy, nên số "·3" trên lịch đội khớp số thẻ ở lịch cá nhân. Đừng viết lại ở đây.
 */
export function occupiesDay(item: ScheduleItem, day: string): boolean {
  return sharedOccupiesDay(item, day);
}

/** Ngày đầu tháng chứa `date`. */
export function startOfMonth(date: string): string {
  const ms = parseIso(date);
  const d = new Date(ms);
  return toIso(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

/**
 * Cộng tháng, giữ nguyên ngày trong tháng khi có thể.
 *
 * 31/01 + 1 tháng: tháng 2 không có ngày 31. Kẹp về ngày cuối tháng thay vì để
 * nó tràn sang tháng 3 — người bấm "tháng sau" không mong nhảy hai tháng.
 * Cùng logic với `lib/date.ts#addMonths`, chỉ khác chữ ký (chuỗi ISO thay vì
 * `Date`) để khớp quy ước "mọi ngày trong feature này là chuỗi" của cả file.
 */
export function addMonths(date: string, months: number): string {
  const d = new Date(parseIso(date));
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + months;
  const day = d.getUTCDate();
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return toIso(Date.UTC(year, month, Math.min(day, lastDay)));
}

/**
 * Lưới sáu tuần cho tháng chứa `date`, luôn **42 ô**.
 *
 * Cố định số hàng chứ không co giãn theo tháng: một lịch cao 5 hàng ở tháng
 * này và 6 hàng ở tháng kia sẽ nhảy giật mỗi lần bấm "tháng sau".
 *
 * ⚠️ Cột đầu là **Chủ nhật**, khác `startOfWeek` (thứ Hai) mà lịch tuần dùng.
 * Hai lưới cố tình khác nhau: lịch tuần là bảy ngày làm việc liền mạch nên
 * cuối tuần đứng cuối mới đọc được, còn lưới tháng là một tờ lịch treo tường —
 * và tờ lịch tiếng Việt bắt đầu từ Chủ nhật.
 */
export function monthGrid(date: string): string[] {
  const first = startOfMonth(date);
  const offset = new Date(parseIso(first)).getUTCDay();
  const start = addDays(first, -offset);
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

/** Hai cách xem lịch — dùng chung giữa route và các component vẽ lưới. */
export const SCHEDULE_UNITS = ['week', 'month'] as const;
export type ScheduleUnit = (typeof SCHEDULE_UNITS)[number];

/**
 * Khoảng ngày cần tải cho một đơn vị xem, tính từ một ngày neo bất kỳ trong đó.
 *
 * Một hàm thuần duy nhất cho cả truy vấn dữ liệu lẫn vẽ lưới, để "khoảng nào
 * được tải" và "khoảng nào được vẽ" không lệch nhau — lệch thì góc lưới hiện ra
 * trống dù dữ liệu ngày đó có thật, chỉ vì query không xin nó.
 */
export function periodRange(anchor: string, unit: ScheduleUnit): { from: string; to: string } {
  if (unit === 'week') {
    const from = startOfWeek(anchor);
    return { from, to: addDays(from, 6) };
  }
  const grid = monthGrid(anchor);
  return { from: grid[0]!, to: grid[41]! };
}

/**
 * Ngày neo mới sau một cú bấm mũi tên — cùng một cặp nút cho cả hai đơn vị
 * xem, xem `my-tasks.tsx`: "chuyển sang dạng mũi tên điều hướng để có thể dùng
 * với cả cách view theo tuần và theo tháng".
 */
export function shiftPeriod(anchor: string, unit: ScheduleUnit, direction: 1 | -1): string {
  return unit === 'week' ? addDays(anchor, 7 * direction) : addMonths(anchor, direction);
}
